<template>
  <div>
    <UILoadingBar />
    <UINetworkStatus />
    <RouterView v-if="!authStore.initializing" v-slot="{ Component }">
      <Transition name="fade" mode="out-in">
        <Suspense>
          <template #default>
            <div>
              <component :is="Component" />
            </div>
          </template>
          <template #fallback>
            <div class="flex items-center justify-center min-h-screen">
              <UIPreloader />
            </div>
          </template>
        </Suspense>
      </Transition>
    </RouterView>
    <div v-else class="flex items-center justify-center min-h-screen">
      <UIPreloader />
    </div>
    <UIToast />
    
    <!-- Add Login Modal -->
    <LoginModal
      :show="isLoginPopupVisible"
      :context="loginPopupContext"
      @close="hideLoginPopup"
      @success="handleLoginSuccess"
      @error="handleLoginError"
      @continueAsGuest="handleContinueAsGuest"
    />
    
    <!-- Share Modal -->
    <ShareModal />

    <AgeVerificationModal />

    <!-- Cookie Consent Banner -->
    <UICookieConsent />
  </div>
</template>

<script setup lang="ts">
import { logger, createLogger } from '@/utils/logger'
// Create a namespaced logger
const fileLogger = createLogger('App')
import { onMounted, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { getConfig } from '@/config'
import UIToast from '@/components/ui/UIToast.vue'
import UILoadingBar from '@/components/ui/UILoadingBar.vue'
import UIPreloader from '@/components/ui/UIPreloader.vue'
import UINetworkStatus from '@/components/ui/UINetworkStatus.vue'
import UICookieConsent from '@/components/ui/UICookieConsent.vue'
import { useAuthStore } from '@/stores/auth'
import { ServiceContainer } from '@/services/service-container'
import { VersionService } from '@/services/version.service'
import LoginModal from '@/components/modals/LoginModal.vue'
import ShareModal from '@/components/modals/ShareModal.vue'
import AgeVerificationModal from '@/components/modals/AgeVerificationModal.vue'
import { useLibraryStore } from '@/stores/library'
import { useLoginPopup, provideLoginPopup } from '@/composables/useLoginPopup'
import { usePlayerStore } from '@/stores/player'
import { retryHandler } from '@/utils/retryHandler'
import { useLocaleDetection } from '@/composables/useLocaleDetection'
import { setLocale } from '@/i18n'
import type { SupportedLocale } from '@/composables/useLocaleDetection'
import { useCookieConsent } from '@/composables/useCookieConsent'
import { initAnalytics, updateConsent } from '@/lib/analytics'

const { consentValue } = useCookieConsent()
const config = getConfig()
const themeStore = useThemeStore()
const authStore = useAuthStore()
const libraryStore = useLibraryStore()
const router = useRouter()
const playerStore = usePlayerStore()

watch(consentValue, (value) => {
  if (value === 'accepted') updateConsent(true)
  else if (value === 'rejected') updateConsent(false)
})

// Initialize locale detection
const { detectLocale, setLocale: setDetectedLocale } = useLocaleDetection({
  useIpGeolocation: false, // Set to true to enable IP-based detection
  prioritizeStoredPreference: true
})

// Use the login popup
const {
  isLoginPopupVisible,
  loginPopupContext,
  showLoginPopup,
  hideLoginPopup,
  handleLoginSuccess: completeLoginPopupSuccess,
  cancelRedirect
} = useLoginPopup()

// After modal login: run redirect logic, then hydrate library (skipped on cold guest start)
const handleLoginSuccess = async () => {
  completeLoginPopupSuccess()
  if (!authStore.isAuthenticated) return
  try {
    fileLogger.log('Loading library data after popup login')
    await libraryStore.loadLibraryData()
  } catch (error) {
    fileLogger.error('Failed to load library after popup login:', error)
  }
}

// Handle continue as guest
const handleContinueAsGuest = () => {
  fileLogger.log('User chose to continue as guest')
  hideLoginPopup()
  // Cancel any pending redirects
  cancelRedirect()
}

// Handle login error
const handleLoginError = (error: Error | unknown) => {
  fileLogger.log('Login error:', error)
  // Cancel any pending redirects to prevent unwanted navigation
  cancelRedirect()
  // Protect against unwanted redirects that might happen after error
  const currentPath = window.location.pathname
  const loginPath = '/login'
  // If we're being redirected to login page due to error, prevent it
  if (currentPath === loginPath && isLoginPopupVisible.value) {
    fileLogger.log('Detected unwanted redirect to login after popup error, going back')
    // Go back to previous page
    setTimeout(() => {
      router.back()
    }, 100)
  }
}

// Provide login popup functionality to all components
provideLoginPopup()

// For testing purposes - expose the login popup functions globally
if (import.meta.env.DEV) {
  (window as any).__testHelpers = {
    showLoginPopup,
    hideLoginPopup,
    isLoginPopupVisible
  }
}

/**
 * Map user language setting to SupportedLocale
 * @param language - Language code from user settings (e.g., 'en', 'es', 'fr')
 */
function mapUserLanguageToLocale(language: string): SupportedLocale | null {
  if (!language) return null
  
  const langLower = language.toLowerCase()
  
  // Map common language codes to supported locales
  if (langLower === 'en' || langLower.startsWith('en-')) {
    // Default to en-GB unless specifically en-US
    return langLower.includes('us') || langLower === 'en-us' ? 'en-US' : 'en-GB'
  }
  if (langLower === 'es' || langLower.startsWith('es-')) return 'es-ES'
  if (langLower === 'fr' || langLower.startsWith('fr-')) return 'fr-FR'
  if (langLower === 'de' || langLower.startsWith('de-')) return 'de-DE'
  if (langLower === 'it' || langLower.startsWith('it-')) return 'it-IT'
  if (langLower === 'pt' || langLower.startsWith('pt-')) return 'pt-PT'
  if (langLower === 'nl' || langLower.startsWith('nl-')) return 'nl-NL'
  
  // Try to match full locale code
  const localeMap: Record<string, SupportedLocale> = {
    'en-gb': 'en-GB',
    'en-us': 'en-US',
    'es-es': 'es-ES',
    'fr-fr': 'fr-FR',
    'de-de': 'de-DE',
    'it-it': 'it-IT',
    'pt-pt': 'pt-PT',
    'nl-nl': 'nl-NL'
  }
  
  return localeMap[langLower] || null
}

/**
 * Initialize locale based on location detection and preferences
 */
async function initializeLocale(): Promise<void> {
  try {
    const detected = await detectLocale()
    setLocale(detected)
    fileLogger.log(`Locale initialized: ${detected}`)
  } catch (error) {
    fileLogger.warn('Failed to initialize locale, using default:', error)
    setLocale('en-GB')
  }
}

// Check for sprite file
const checkSpriteExists = () => {
  if (import.meta.env.DEV) {
    fetch(import.meta.env.BASE_URL ? `${import.meta.env.BASE_URL}sprite.svg` : '/sprite.svg')
      .then(response => {
        if (!response.ok) {
          console.error('SVG sprite file not found! Icons may not display correctly. Run "npm run generate-svg-sprite" to generate it.')
        }
      })
      .catch(err => {
        console.error('Error loading SVG sprite:', err)
      })
  }
}

onMounted(async () => {
  initAnalytics(
    consentValue.value === 'accepted'
      ? true
      : consentValue.value === 'rejected'
        ? false
        : null,
  )

  // Check if sprite exists
  checkSpriteExists()
  
  // Set up network retry handling
  window.addEventListener('network-retry', async () => {
    fileLogger.log('Network retry event received, processing queued operations')
    await retryHandler.processQueuedOperations()
  })
  
  const services = ServiceContainer.getInstance()
  const versionService = await services.get<VersionService>('version')
  // Check version and clear cache if needed
  const isNewVersion = await versionService.checkVersion()
  if (isNewVersion) {
    // Force reload app to ensure clean state
    window.location.reload()
    return
  }
  themeStore.init()
  document.title = config.app.name
  
  // Initialize locale detection and set locale
  await initializeLocale()
  
  // Initialize authentication
  await authStore.initializeAuth()
  
  // Check if user has language preference and update locale if needed
  if (authStore.isAuthenticated && authStore.user?.settings?.language) {
    const userLanguage = mapUserLanguageToLocale(authStore.user.settings.language)
    if (userLanguage) {
      setLocale(userLanguage)
      setDetectedLocale(userLanguage)
      fileLogger.log(`Locale set from user preference: ${userLanguage}`)
    }
  }
  
  // Only load library data for authenticated users
  if (authStore.isAuthenticated) {
    fileLogger.log('User is authenticated, loading library data')
    await libraryStore.loadLibraryData()
    // For authenticated users, prepare the last played title without playing it
    fileLogger.log('Preparing last played title')
    await playerStore.prepareLastPlayedTitle()
  } else {
    fileLogger.log('User is not authenticated, skipping library data loading')
  }

  // Flush in-memory progress before unload so reload resume is not stale
  const flushPlaybackOnHide = () => {
    if (!authStore.isAuthenticated || playerStore.isPreviewMode) return
    if (!playerStore.currentTitle || !playerStore.currentTrack) return
    if (playerStore.currentTime <= 0) return
    playerStore.updatePlaybackPosition(playerStore.currentTime)
    void playerStore.savePlaybackState()
    void playerStore.syncPlaybackPosition()
  }
  window.addEventListener('pagehide', flushPlaybackOnHide)
  window.addEventListener('beforeunload', flushPlaybackOnHide)
})
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom Scrollbar Styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* For Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  ::-webkit-scrollbar-thumb {
    background: #475569;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: #64748b;
  }
  
  * {
    scrollbar-color: #475569 transparent;
  }
}

/* Global SVG color inheritance */
svg {
  color: inherit;
}
</style>